import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  getCoachCompanyDetail,
  upsertMilestone,
  deleteMilestone,
  addCoachNote,
  deleteCoachNote,
  updateCompanyBasics,
} from "@/lib/coach-portal.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, FileText, Target, NotebookPen } from "lucide-react";
import { MONTH_NAMES_SV, TRL_LABELS } from "@/lib/monthly-report";
import { toast } from "sonner";

export const Route = createFileRoute("/coach-portal/$companyId")({
  component: CompanyDetail,
});

const STATUSES = ["planerad", "pågående", "klar", "pausad"] as const;

function CompanyDetail() {
  const { companyId } = Route.useParams();
  const fetchDetail = useServerFn(getCoachCompanyDetail);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["coach-portal", "company", companyId],
    queryFn: () => fetchDetail({ data: { companyId } }),
  });

  if (isLoading) return <div className="p-8 text-sm text-muted-foreground">Laddar…</div>;
  if (error) return <div className="p-8 text-sm text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const { company, reports, milestones, notes } = data;
  const refetch = () => qc.invalidateQueries({ queryKey: ["coach-portal", "company", companyId] });

  return (
    <div className="px-6 md:px-10 py-10 max-w-5xl">
      <Link to="/coach-portal" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="size-3.5" /> Mina bolag
      </Link>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl">{company.name}</h1>
          <div
            className="text-sm text-muted-foreground mt-1"
            data-testid="company-phase-status"
          >
            <span data-testid="company-fas">{company.fas}</span>
            {" · "}
            <span data-testid="company-status">{company.status}</span>
            {company.municipality ? (
              <>
                {" · "}
                <span data-testid="company-municipality">{company.municipality}</span>
              </>
            ) : null}
          </div>
        </div>
        <Link
          to="/coach-portal/$companyId/rapport"
          params={{ companyId }}
          className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2"
        >
          <Plus className="size-4" /> Ny / uppdatera månadsrapport
        </Link>
      </div>

      <BasicsSection company={company} onSaved={refetch} />

      <Section icon={<Target className="size-4" />} title="Milstolpar & målplan">
        <MilestonesEditor companyId={companyId} milestones={milestones} onChanged={refetch} />
      </Section>

      <Section icon={<FileText className="size-4" />} title="Månadsrapporter & TRL-utveckling">
        {reports.length === 0 ? (
          <p className="text-sm text-muted-foreground">Inga rapporter ännu.</p>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <div key={r.id} className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-sm">
                    {MONTH_NAMES_SV[r.month - 1]} {r.year}
                  </div>
                  <div className="text-xs text-muted-foreground">{r.coach_name}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                  {(["crl", "brl", "tmrl", "srl"] as const).map((k) => (
                    <div key={k} className="rounded-lg bg-secondary px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {TRL_LABELS[k].split(" — ")[0]}
                      </div>
                      <div className="font-display text-lg">{r[k] ?? "—"}</div>
                    </div>
                  ))}
                </div>
                {r.summary && <p className="text-sm text-muted-foreground">{r.summary}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={<NotebookPen className="size-4" />} title="Privata anteckningar">
        <NotesEditor companyId={companyId} notes={notes} onChanged={refetch} />
      </Section>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6 mb-6">
      <h2 className="font-display text-lg mb-4 flex items-center gap-2">
        {icon} {title}
      </h2>
      {children}
    </section>
  );
}

function BasicsSection({
  company,
  onSaved,
}: {
  company: { id: string; fas: string; status: string; municipality: string | null; contact_name: string | null; contact_email: string | null };
  onSaved: () => void;
}) {
  const update = useServerFn(updateCompanyBasics);
  const [form, setForm] = useState({
    fas: company.fas,
    status: company.status,
    municipality: company.municipality ?? "",
    contact_name: company.contact_name ?? "",
    contact_email: company.contact_email ?? "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await update({
        data: {
          id: company.id,
          fas: form.fas,
          status: form.status,
          municipality: form.municipality || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
        },
      });
      toast.success("Sparat");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Fel");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section icon={<FileText className="size-4" />} title="Bolagsuppgifter">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Fas"><Input value={form.fas} onChange={(e) => setForm({ ...form, fas: e.target.value })} /></Field>
        <Field label="Status"><Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} /></Field>
        <Field label="Kommun"><Input value={form.municipality} onChange={(e) => setForm({ ...form, municipality: e.target.value })} /></Field>
        <Field label="Kontaktperson"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
        <Field label="Kontakt-e-post"><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
      </div>
      <div className="mt-4">
        <Button onClick={save} disabled={saving} size="sm">{saving ? "Sparar…" : "Spara uppgifter"}</Button>
      </div>
    </Section>
  );
}

type Milestone = {
  id: string;
  title: string;
  description: string | null;
  target_date: string | null;
  status: string;
};

function MilestonesEditor({
  companyId,
  milestones,
  onChanged,
}: {
  companyId: string;
  milestones: Milestone[];
  onChanged: () => void;
}) {
  const upsert = useServerFn(upsertMilestone);
  const remove = useServerFn(deleteMilestone);
  const [draft, setDraft] = useState({ title: "", description: "", target_date: "", status: "planerad" as typeof STATUSES[number] });
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!draft.title.trim()) return;
    setBusy(true);
    try {
      await upsert({
        data: {
          company_id: companyId,
          title: draft.title.trim(),
          description: draft.description || null,
          target_date: draft.target_date || null,
          status: draft.status,
        },
      });
      setDraft({ title: "", description: "", target_date: "", status: "planerad" });
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Fel");
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(m: Milestone, status: typeof STATUSES[number]) {
    await upsert({
      data: {
        id: m.id,
        company_id: companyId,
        title: m.title,
        description: m.description,
        target_date: m.target_date,
        status,
      },
    });
    onChanged();
  }

  return (
    <div>
      {milestones.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-4">Inga milstolpar ännu.</p>
      ) : (
        <ul className="space-y-2 mb-4">
          {milestones.map((m) => (
            <li key={m.id} className="rounded-xl border border-border p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{m.title}</div>
                {m.description && <div className="text-xs text-muted-foreground mt-0.5">{m.description}</div>}
                <div className="text-[11px] text-muted-foreground mt-1">
                  {m.target_date ? `Mål: ${m.target_date}` : "Inget måldatum"}
                </div>
              </div>
              <select
                value={m.status}
                onChange={(e) => changeStatus(m, e.target.value as typeof STATUSES[number])}
                className="h-8 px-2 rounded-md bg-secondary text-xs"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={async () => { await remove({ data: { id: m.id } }); onChanged(); }}
                className="text-muted-foreground hover:text-destructive p-1"
                aria-label="Ta bort"
              >
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-xl border border-dashed border-border p-4 space-y-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Ny milstolpe</div>
        <Input placeholder="Titel" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        <Textarea placeholder="Beskrivning (valfritt)" rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input type="date" value={draft.target_date} onChange={(e) => setDraft({ ...draft, target_date: e.target.value })} />
          <select
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value as typeof STATUSES[number] })}
            className="h-10 px-3 rounded-md bg-secondary text-sm"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Button onClick={add} disabled={busy} size="sm" className="w-full">
          <Plus className="size-4 mr-1" /> Lägg till
        </Button>
      </div>
    </div>
  );
}

type Note = { id: string; body: string; created_at: string };

function NotesEditor({
  companyId,
  notes,
  onChanged,
}: {
  companyId: string;
  notes: Note[];
  onChanged: () => void;
}) {
  const addNote = useServerFn(addCoachNote);
  const removeNote = useServerFn(deleteCoachNote);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await addNote({ data: { company_id: companyId, body: body.trim() } });
      setBody("");
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Fel");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">
        Anteckningarna är privata och endast synliga för dig.
      </p>
      {notes.length > 0 && (
        <ul className="space-y-2 mb-4">
          {notes.map((n) => (
            <li key={n.id} className="rounded-xl border border-border p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-muted-foreground mb-1">
                  {new Date(n.created_at).toLocaleString("sv-SE")}
                </div>
                <div className="text-sm whitespace-pre-wrap">{n.body}</div>
              </div>
              <button
                onClick={async () => { await removeNote({ data: { id: n.id } }); onChanged(); }}
                className="text-muted-foreground hover:text-destructive p-1"
                aria-label="Ta bort"
              >
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Textarea rows={3} placeholder="Skriv en anteckning…" value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="mt-2">
        <Button onClick={save} disabled={busy} size="sm">
          <Plus className="size-4 mr-1" /> Spara anteckning
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
